import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ClaudeMessage } from "@/utils/axios/claudeUtils.ts";
import { ArrowLeft, CheckCircle, Edit, Menu, X } from "lucide-react";
import ChatInput from "@/pages/API/ClaudeChat/parts/ChatInput.tsx";
import MessageItem from "@/pages/API/ClaudeChat/parts/MessageItem.tsx";

interface ClaudeChatMainProps {
  messages: ClaudeMessage[];
  isLoading: boolean;
  conversationTitle: string;
  showSidebar: boolean;
  isMobileView: boolean;
  onToggleSidebar: () => void;
  onSendMessage: (message: string) => Promise<void>;
  onRenameConversation: (newTitle: string) => Promise<void>;
  onBackToList: () => void;
}

const ClaudeChatMain: React.FC<ClaudeChatMainProps> = ({
  messages,
  isLoading,
  conversationTitle,
  showSidebar,
  isMobileView,
  onToggleSidebar,
  onSendMessage,
  onRenameConversation,
  onBackToList,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [showWelcome, setShowWelcome] = useState(messages.length === 0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Ajuster la hauteur du conteneur de messages
  useEffect(() => {
    const adjustHeight = () => {
      if (
        chatContainerRef.current &&
        headerRef.current &&
        chatInputRef.current
      ) {
        const headerHeight = headerRef.current.clientHeight;
        const inputHeight = chatInputRef.current.clientHeight;
        const windowHeight = window.innerHeight;

        // Calculer la hauteur disponible pour le conteneur de messages
        const availableHeight = windowHeight - headerHeight - inputHeight;

        // Appliquer la hauteur
        chatContainerRef.current.style.height = `${availableHeight}px`;
      }
    };

    // Ajuster la hauteur initiale
    adjustHeight();

    // Ajuster la hauteur lors du redimensionnement
    window.addEventListener("resize", adjustHeight);

    // Nettoyer
    return () => {
      window.removeEventListener("resize", adjustHeight);
    };
  }, []);

  // Scroll vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Mise à jour du titre lors de l'édition
  useEffect(() => {
    setEditTitle(conversationTitle);
  }, [conversationTitle]);

  // Masquer le message de bienvenue quand il y a des messages
  useEffect(() => {
    setShowWelcome(messages.length === 0);
  }, [messages]);

  const handleTitleSubmit = async () => {
    await onRenameConversation(editTitle);
    setIsEditing(false);
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="flex flex-col h-full">
      {/* En-tête */}
      <motion.div
        ref={headerRef}
        className="sticky top-0 z-10 p-3 md:p-4 border-b border-base-200 bg-base-100 flex items-center gap-2"
        initial="hidden"
        animate="visible"
        variants={headerVariants}
        transition={{ duration: 0.3 }}
      >
        {isMobileView ? (
          <button
            onClick={onBackToList}
            className="btn btn-ghost btn-sm btn-square"
            aria-label="Retour à la liste"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={onToggleSidebar}
            className="btn btn-ghost btn-sm btn-square"
            aria-label={
              showSidebar
                ? "Masquer la barre latérale"
                : "Afficher la barre latérale"
            }
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {isEditing ? (
          <div className="flex-1 flex items-center">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="input input-bordered flex-1 h-8 text-base py-1"
              autoFocus
            />
            <button
              onClick={handleTitleSubmit}
              className="btn btn-ghost btn-sm ml-1"
              aria-label="Enregistrer"
            >
              <CheckCircle className="w-5 h-5 text-green-600" />
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="btn btn-ghost btn-sm"
              aria-label="Annuler"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-base md:text-lg font-medium flex-1 truncate">
              {conversationTitle || "Nouvelle conversation"}
            </h1>
            {conversationTitle && (
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-ghost btn-sm"
                aria-label="Renommer"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
          </>
        )}
      </motion.div>

      {/* Zone de messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-2 md:p-4"
        style={{ overscrollBehavior: "contain", minHeight: "0" }}
      >
        <AnimatePresence>
          {showWelcome ? (
            <motion.div
              className="h-full flex flex-col items-center justify-center text-center p-4 md:p-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-purple-100 flex items-center justify-center mb-3 md:mb-4">
                <svg
                  viewBox="0 0 24 24"
                  className="w-6 h-6 md:w-8 md:h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 4h2.5a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5h-2.5"></path>
                  <path d="M5 10h2.5a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5h-2.5"></path>
                  <path d="M13 4h6"></path>
                  <path d="M13 10h6"></path>
                  <path d="M13 16h6"></path>
                  <path d="M5 16h2.5a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5h-2.5"></path>
                </svg>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold mb-2">
                Comment puis-je vous aider aujourd'hui ?
              </h2>
              <p className="text-gray-600 max-w-md mb-4 md:mb-6 text-sm md:text-base">
                Je suis Claude, votre assistant IA. Je peux vous aider avec vos
                questions, générer du contenu, analyser des données, et bien
                plus encore.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 max-w-xl w-full">
                {[
                  "Écris un message marketing pour un nouveau produit",
                  "Aide-moi à résoudre ce problème mathématique",
                  "Résume cet article scientifique",
                  "Explique-moi ce concept complexe",
                ].map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => onSendMessage(suggestion)}
                    className="btn btn-outline border-purple-200 hover:bg-purple-50 hover:border-purple-300 normal-case text-left justify-start h-auto py-2 text-sm"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <div className="space-y-2 md:space-y-4 max-w-4xl mx-auto">
              {messages.map((msg, index) => (
                <MessageItem key={index} message={msg} />
              ))}
              {isLoading && (
                <motion.div
                  className="flex items-center p-2 md:p-4 gap-2 text-gray-600"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className="bg-purple-600 rounded-full"
                    initial={{ width: "0.6rem", height: "0.6rem" }}
                    animate={{
                      width: ["0.6rem", "0.9rem", "0.6rem"],
                      height: ["0.6rem", "0.9rem", "0.6rem"],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeInOut",
                    }}
                  />
                  <motion.div
                    className="bg-purple-400 rounded-full"
                    initial={{ width: "0.6rem", height: "0.6rem" }}
                    animate={{
                      width: ["0.6rem", "0.9rem", "0.6rem"],
                      height: ["0.6rem", "0.9rem", "0.6rem"],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeInOut",
                      delay: 0.2,
                    }}
                  />
                  <motion.div
                    className="bg-purple-300 rounded-full"
                    initial={{ width: "0.6rem", height: "0.6rem" }}
                    animate={{
                      width: ["0.6rem", "0.9rem", "0.6rem"],
                      height: ["0.6rem", "0.9rem", "0.6rem"],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeInOut",
                      delay: 0.4,
                    }}
                  />
                  <motion.span
                    className="text-xs md:text-sm text-gray-500 ml-2"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{
                      repeat: Infinity,
                      duration: 2,
                    }}
                  >
                    Claude réfléchit...
                  </motion.span>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Zone de saisie */}
      <div ref={chatInputRef}>
        <ChatInput onSendMessage={onSendMessage} isLoading={isLoading} />
      </div>
    </div>
  );
};

export default ClaudeChatMain;
